import { useEffect, useRef, useState } from "react";
import { Ruler, Camera, CheckCircle, X, Cube } from "@phosphor-icons/react";
import { RadarMark } from "./FieldRadarLogo";

const WIDTHS = [23, 27, 29, 32, 34, 36, 48];
const HEIGHTS = [64, 72];
const M_TO_IN = 39.3701;

/**
 * WebXR immersive-ar + hit-test tape measure (Android Chrome / ARCore).
 * Tap once for the start point, once for the end → inches (1 decimal).
 * Returns null when AR is unavailable so the caller shows the chip fallback.
 */
async function arSupported() {
  try { return !!(navigator.xr && await navigator.xr.isSessionSupported("immersive-ar")); } catch { return false; }
}

async function measureWithAR(onStatus) {
  const overlay = document.createElement("div");
  overlay.style.cssText = "position:fixed;inset:0;display:flex;flex-direction:column;justify-content:space-between;pointer-events:none;font-family:sans-serif;color:#fff";
  overlay.innerHTML = `<div style="margin:16px;padding:12px 16px;background:rgba(11,28,45,.8);border-radius:14px;font-weight:800;font-size:18px" id="ar-msg">Point at the window edge. Tap to set the START point.</div>
    <div style="display:flex;justify-content:center;pointer-events:none"><div style="width:22px;height:22px;border:3px solid #14b8a6;border-radius:50%;box-shadow:0 0 0 2px rgba(0,0,0,.4)"></div></div>
    <div style="margin:16px;display:flex;gap:10px;pointer-events:auto"><button id="ar-cancel" style="flex:1;height:52px;border-radius:14px;border:0;background:rgba(255,255,255,.15);color:#fff;font-weight:800;font-size:16px">Cancel</button><button id="ar-reset" style="flex:1;height:52px;border-radius:14px;border:0;background:#14b8a6;color:#0b1c2d;font-weight:800;font-size:16px">Reset</button></div>`;
  document.body.appendChild(overlay);

  const canvas = document.createElement("canvas");
  const gl = canvas.getContext("webgl", { xrCompatible: true });
  const session = await navigator.xr.requestSession("immersive-ar", {
    requiredFeatures: ["hit-test"], optionalFeatures: ["dom-overlay"], domOverlay: { root: overlay } });
  await gl.makeXRCompatible();
  session.updateRenderState({ baseLayer: new XRWebGLLayer(session, gl) });
  const refSpace = await session.requestReferenceSpace("local");
  const viewerSpace = await session.requestReferenceSpace("viewer");
  const hitSource = await session.requestHitTestSource({ space: viewerSpace });

  let lastHit = null; const pts = [];
  const msg = overlay.querySelector("#ar-msg");
  return new Promise((resolve) => {
    const finish = (val) => { try { session.end(); } catch {} overlay.remove(); resolve(val); };
    overlay.querySelector("#ar-cancel").onclick = () => finish(null);
    overlay.querySelector("#ar-reset").onclick = () => { pts.length = 0; msg.textContent = "Reset. Tap to set the START point."; };
    session.addEventListener("select", () => {
      if (!lastHit) { msg.textContent = "No surface yet — move the phone slowly over the window frame."; return; }
      pts.push(lastHit);
      if (pts.length === 1) { msg.textContent = "Start set. Now aim at the other edge and tap for the END point."; onStatus?.("start"); }
      if (pts.length === 2) {
        const [a, b] = pts;
        const d = Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z) * M_TO_IN;
        msg.textContent = `${d.toFixed(1)} in`;
        setTimeout(() => finish(Math.round(d * 10) / 10), 700);
      }
    });
    session.addEventListener("end", () => { overlay.remove(); resolve(pts.length === 2 ? null : null); });
    const onFrame = (t, frame) => {
      session.requestAnimationFrame(onFrame);
      const layer = session.renderState.baseLayer;
      gl.bindFramebuffer(gl.FRAMEBUFFER, layer.framebuffer);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
      const hits = frame.getHitTestResults(hitSource);
      if (hits.length) { const p = hits[0].getPose(refSpace); if (p) lastHit = { x: p.transform.position.x, y: p.transform.position.y, z: p.transform.position.z }; }
    };
    session.requestAnimationFrame(onFrame);
  });
}

function Chips({ options, value, onPick, custom, setCustom, testid }) {
  const isCustom = value != null && !options.includes(value);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(o => (
        <button key={o} onClick={() => { onPick(o); setCustom(""); }} data-testid={`${testid}-${o}`}
          className={`h-12 min-w-[60px] px-4 rounded-full border-2 font-black text-lg ${value === o ? "bg-teal-600 border-teal-600 text-white" : "border-slate-300 text-slate-800 bg-white"}`}>{o}</button>
      ))}
      <div className={`h-12 rounded-full border-2 flex items-center px-3 gap-1 ${isCustom ? "bg-teal-600 border-teal-600 text-white" : "border-slate-300 bg-white"}`}>
        <input inputMode="decimal" placeholder="Custom" value={custom} data-testid={`${testid}-custom`}
          onChange={e => { setCustom(e.target.value); const n = parseFloat(e.target.value); if (!isNaN(n)) onPick(Math.round(n * 10) / 10); }}
          className={`w-20 bg-transparent outline-none font-black text-lg ${isCustom ? "text-white placeholder-white/70" : "text-slate-800"}`}/>
        <span className={`text-xs font-bold ${isCustom ? "text-white" : "text-slate-500"}`}>in</span>
      </div>
    </div>
  );
}

export default function BlindsSheet({ roomName, existing, onSave, onClose, onPhoto }) {
  const [w, setW] = useState(existing?.width_in ?? null);
  const [h, setH] = useState(existing?.height_in ?? null);
  const [cw, setCw] = useState(existing?.width_in && !WIDTHS.includes(existing.width_in) ? String(existing.width_in) : "");
  const [ch, setCh] = useState(existing?.height_in && !HEIGHTS.includes(existing.height_in) ? String(existing.height_in) : "");
  const [ar, setAr] = useState(null);
  const [measuring, setMeasuring] = useState(null);
  const [method, setMethod] = useState(existing?.method || "chips");
  const [photo, setPhoto] = useState(existing?.photo_path || null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { arSupported().then(setAr); }, []);

  const runAR = async (dim) => {
    setMeasuring(dim);
    try {
      const inches = await measureWithAR();
      if (inches != null) { (dim === "w" ? setW : setH)(inches); (dim === "w" ? setCw : setCh)(String(inches)); setMethod("ar"); }
    } catch { setAr(false); }
    finally { setMeasuring(null); }
  };

  const save = async () => {
    setSaving(true);
    try { await onSave({ width_in: w, height_in: h, method: method === "ar" ? "ar" : ((cw || ch) ? "custom" : "chips"), photo_path: photo }); onClose(); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end bg-black/60" data-testid="blinds-sheet">
      <div className="bg-white rounded-t-3xl p-5 pb-8 max-h-[92vh] overflow-y-auto">
        <div className="mx-auto w-14 h-1.5 rounded-full bg-slate-300 mb-4"/>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-2xl font-black text-[#0b1c2d]">Blinds need replaced</div>
            <div className="text-sm text-slate-500">{roomName} · sizes go to the turn crew</div>
          </div>
          <button onClick={onClose} className="w-10 h-10 -mr-2 flex items-center justify-center" data-testid="blinds-close"><X size={22}/></button>
        </div>

        {ar && (
          <button onClick={() => runAR(w == null ? "w" : "h")} disabled={!!measuring} data-testid="blinds-measure-ar"
            className="mt-4 w-full h-14 rounded-2xl bg-[#0b1c2d] text-white font-black text-lg flex items-center justify-center gap-2 disabled:opacity-50">
            <Cube weight="fill" size={22} className="text-teal-400"/> {measuring ? "Measuring…" : `Measure ${w == null ? "width" : "height"} with camera`}
          </button>
        )}
        {ar === false && <div className="mt-3 text-xs text-slate-500">AR tape measure needs Android Chrome — pick a size below or type it.</div>}

        <div className="mt-5 flex items-center gap-2 text-[#0b1c2d] font-bold"><Ruler size={20}/> Width (inches)
          {w != null && <span className="ml-auto text-teal-700 font-black" data-testid="blinds-width-value">{w} in</span>}</div>
        <div className="mt-2"><Chips options={WIDTHS} value={w} onPick={setW} custom={cw} setCustom={setCw} testid="blinds-w"/></div>
        {ar && w != null && <button onClick={() => runAR("w")} className="mt-1 text-xs text-teal-700 font-bold underline">Re-measure width with camera</button>}

        <div className="mt-5 flex items-center gap-2 text-[#0b1c2d] font-bold"><Ruler size={20} className="rotate-90"/> Height (inches)
          {h != null && <span className="ml-auto text-teal-700 font-black" data-testid="blinds-height-value">{h} in</span>}</div>
        <div className="mt-2"><Chips options={HEIGHTS} value={h} onPick={setH} custom={ch} setCustom={setCh} testid="blinds-h"/></div>
        {ar && h != null && <button onClick={() => runAR("h")} className="mt-1 text-xs text-teal-700 font-bold underline">Re-measure height with camera</button>}

        <div className="mt-5 flex items-center gap-2 text-[#0b1c2d] font-bold"><Camera size={20}/> Photo (optional)</div>
        <button onClick={async () => { const p = await onPhoto(); if (p) setPhoto(p.path); }} data-testid="blinds-photo"
          className="mt-2 w-full h-24 rounded-2xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 gap-1">
          <Camera size={28} className="text-teal-600"/> <span className="text-sm">{photo ? "Photo added · tap for another" : "Tap to add photo of the window"}</span>
        </button>

        <button onClick={save} disabled={saving || (w == null && h == null)} data-testid="blinds-save"
          className="mt-5 w-full h-14 rounded-2xl bg-teal-600 disabled:bg-slate-300 text-white font-black text-lg flex items-center justify-center gap-2">
          <CheckCircle weight="fill" size={24}/> Use these sizes
        </button>
        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-slate-500"><RadarMark size={16}/> Saved onto {roomName} for the turn crew.</div>
      </div>
    </div>
  );
}
